import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/core'

/** Skip link target id, passed to `Root` as `skipLinkId`, so "Skip to Main Content" bypasses the header and the sub nav. */
export const SEARCH_CONTENT_ID = 'search-content'

const targetCss = css`
  height: 100%;
  outline: none;
`

/**
 * Whether a click came from a link to `#search-content`, like Root's "Skip to Main Content" link.
 * @param {MouseEvent} event - a click event
 * @returns {boolean} true for a skip link click
 */
const isSkipLinkClick = (event) => event.target.closest?.('a')?.hash === `#${SEARCH_CONTENT_ID}`

/**
 * Skip link target that wraps the page content after the sub nav. Always rendered, so the skip link works when the
 * sub nav is hidden. It's focusable only while the skip link sends focus to it, so plain clicks still focus body:
 * a focusable wrapper set off a focus loop in the image viewer.
 * @param {Object} props
 * @param {React.ReactNode} props.children - the page content
 * @returns {JSX.Element} the page content inside the skip link target
 */
export function SearchContentWrapper({ children }) {
  const targetRef = useRef(null)

  useEffect(() => {
    const handleClick = (event) => {
      if (!isSkipLinkClick(event) || !targetRef.current) return
      targetRef.current.setAttribute('tabindex', '-1')
      targetRef.current.focus()
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  return (
    <div
      id={SEARCH_CONTENT_ID}
      ref={targetRef}
      onBlur={(event) => event.currentTarget.removeAttribute('tabindex')}
      css={targetCss}
    >
      {children}
    </div>
  )
}

SearchContentWrapper.propTypes = {
  children: PropTypes.node,
}
